// @flow

import { useMemo } from 'react';

import { selectAuthenticatedUserInfos } from '@gandi/authentication';

import {
  selectProductOptionsUIFactory,
  selectOneProductErrors,
  type DecoratedProduct,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import { selectShouldShowOtherDomainErrors } from '../../../modules/selectors';

import { DomainTransferOption } from './DomainTransferOption';

import { DomainLabel } from './components/DomainLabel';
import ProductItem from './components/ProductItem';
import { FreeMailboxRow } from './components/Rows';
import AuthCodeRow from './components/Rows/AuthCodeRow';
import OwnerRow from './components/Rows/OwnerRow';

type Props = {
  product: DecoratedProduct,
};

function DotUkWarning() {
  const { __ } = useTranslator();

  return (
    <Base color="light">
      {__(
        'In order to transfer in this domain you need to ask prior to this process a push to the tag named “GANDI”. This process have to be done with the CURRENT registrar',
      )}
    </Base>
  );
}

const selectProductOptionsUI = selectProductOptionsUIFactory();

export function DomainTransfer({ product }: Props): React.MixedElement {
  const displayErrors = useAppSelector(selectShouldShowOtherDomainErrors);
  const productOptions = useAppSelector(state =>
    selectProductOptionsUI(state, product.id),
  );
  const authenticated = useAppSelector(selectAuthenticatedUserInfos);

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );
  const filteredErrors = useMemo(
    () => productErrors.filter(error => error.name !== 'authinfo'),
    [productErrors],
  );

  const isDotUK = product.payload.fqdn?.endsWith('.uk');
  const isInternal = product.payload.internal;
  const isAuthInfoNeeded = product.payload.authinfoForTransfer;

  return (
    <ProductItem
      key={`${product.id}-row`}
      hasTaxes={product.hasTaxes}
      priceBeforeTaxes={
        product.isWorking ? undefined : product.total?.before_taxes
      }
      productId={product.id}
      label={<DomainLabel product={product} />}
      isCorporate={product.payload.corporate}
      errors={displayErrors ? filteredErrors : undefined}
    >
      {isDotUK && isInternal && <DotUkWarning />}
      {authenticated && (isInternal || (!isInternal && isAuthInfoNeeded)) && (
        <AuthCodeRow
          authCode={product.payload.authinfo}
          productId={product.id}
        />
      )}

      {product.payload.owner && (
        <OwnerRow
          owner={product.payload.owner}
          productId={product.id}
          canChangeOwner={!!product.payload.change_owner}
        />
      )}

      <FreeMailboxRow product={product} />

      {productOptions.map(option => (
        <DomainTransferOption
          option={option}
          key={`${product.id}-${option.id}`}
        />
      ))}
    </ProductItem>
  );
}
